// src/application-steps/StepContent4.js

import React, { memo } from "react";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import FormRow from "../../helpers/FormRow";
import FormInput from "../../helpers/FormInput";
import FormCheckbox from "../../helpers/FormCheckbox";

function StepContent4({
  formData,
  setFormData,
  stepErrors,
  handleChange,
  isReadOnly,
}) {
  const getStatus = (field, isRequired) => {
    if (stepErrors[field]) {
      return "error";
    }
    if (isRequired && formData[field]) {
      return "success";
    }
    if (!isRequired && !formData[field]) {
      return "warning";
    }
    return "";
  };

  const hasAnyWarnings =
    getStatus("school_grade_10_activities", false) === "warning" ||
    getStatus("school_grade_10_contact_info", false) === "warning" ||
    getStatus("school_grade_10_hours_served", false) === "warning" ||
    getStatus("school_grade_11_activities", false) === "warning" ||
    getStatus("school_grade_11_contact_info", false) === "warning" ||
    getStatus("school_grade_11_hours_served", false) === "warning" ||
    getStatus("school_grade_12_activities", false) === "warning" ||
    getStatus("school_grade_12_contact_info", false) === "warning";

  // Local rows state for multi-entry support (school activities)
  const parseGradeFieldsToRows = (activitiesStr, contactsStr, hoursStr) => {
    // Preserve empty lines so blank rows added by the user are kept
    const actLines = (activitiesStr || "").split(/\r?\n/);
    const contactLines = (contactsStr || "").split(/\r?\n/);
    const hourLines = (hoursStr || "").split(/\r?\n/);

    const maxLen = Math.max(
      actLines.length,
      contactLines.length,
      hourLines.length,
      1
    );
    const rows = [];
    for (let i = 0; i < maxLen; i++) {
      rows.push({
        activity: actLines[i] !== undefined ? actLines[i] : "",
        contact: contactLines[i] !== undefined ? contactLines[i] : "",
        hours: hourLines[i] !== undefined ? hourLines[i] : "",
      });
    }
    return rows;
  };

  const combineRowsToFields = (rows) => {
    const activities = rows.map((r) => r.activity || "").join("\n");
    const contacts = rows.map((r) => r.contact || "").join("\n");
    const hours = rows.map((r) => r.hours || "").join("\n");
    return { activities, contacts, hours };
  };

  const [grade10Rows, setGrade10Rows] = React.useState(() =>
    parseGradeFieldsToRows(
      formData.school_grade_10_activities,
      formData.school_grade_10_contact_info,
      formData.school_grade_10_hours_served
    )
  );
  const [grade11Rows, setGrade11Rows] = React.useState(() =>
    parseGradeFieldsToRows(
      formData.school_grade_11_activities,
      formData.school_grade_11_contact_info,
      formData.school_grade_11_hours_served
    )
  );
  const [grade12Rows, setGrade12Rows] = React.useState(() =>
    parseGradeFieldsToRows(
      formData.school_grade_12_activities,
      formData.school_grade_12_contact_info,
      formData.school_grade_12_hours_served
    )
  );

  // Keep local rows in sync when formData (from parent hydration) changes
  React.useEffect(() => {
    setGrade10Rows(
      parseGradeFieldsToRows(
        formData.school_grade_10_activities,
        formData.school_grade_10_contact_info,
        formData.school_grade_10_hours_served
      )
    );
  }, [
    formData.school_grade_10_activities,
    formData.school_grade_10_contact_info,
    formData.school_grade_10_hours_served,
  ]);

  React.useEffect(() => {
    setGrade11Rows(
      parseGradeFieldsToRows(
        formData.school_grade_11_activities,
        formData.school_grade_11_contact_info,
        formData.school_grade_11_hours_served
      )
    );
  }, [
    formData.school_grade_11_activities,
    formData.school_grade_11_contact_info,
    formData.school_grade_11_hours_served,
  ]);

  React.useEffect(() => {
    setGrade12Rows(
      parseGradeFieldsToRows(
        formData.school_grade_12_activities,
        formData.school_grade_12_contact_info,
        formData.school_grade_12_hours_served
      )
    );
  }, [
    formData.school_grade_12_activities,
    formData.school_grade_12_contact_info,
    formData.school_grade_12_hours_served,
  ]);

  // When local rows change, update the combined fields in parent formData
  React.useEffect(() => {
    const { activities, contacts, hours } = combineRowsToFields(grade10Rows);
    if (
      activities !== formData.school_grade_10_activities ||
      contacts !== formData.school_grade_10_contact_info ||
      hours !== formData.school_grade_10_hours_served
    ) {
      setFormData((prev) => ({
        ...prev,
        school_grade_10_activities: activities,
        school_grade_10_contact_info: contacts,
        school_grade_10_hours_served: hours,
      }));
    }
  }, [
    grade10Rows,
    formData.school_grade_10_activities,
    formData.school_grade_10_contact_info,
    formData.school_grade_10_hours_served,
    setFormData,
  ]);

  React.useEffect(() => {
    const { activities, contacts, hours } = combineRowsToFields(grade11Rows);
    if (
      activities !== formData.school_grade_11_activities ||
      contacts !== formData.school_grade_11_contact_info ||
      hours !== formData.school_grade_11_hours_served
    ) {
      setFormData((prev) => ({
        ...prev,
        school_grade_11_activities: activities,
        school_grade_11_contact_info: contacts,
        school_grade_11_hours_served: hours,
      }));
    }
  }, [
    grade11Rows,
    formData.school_grade_11_activities,
    formData.school_grade_11_contact_info,
    formData.school_grade_11_hours_served,
    setFormData,
  ]);

  React.useEffect(() => {
    const { activities, contacts, hours } = combineRowsToFields(grade12Rows);
    if (
      activities !== formData.school_grade_12_activities ||
      contacts !== formData.school_grade_12_contact_info ||
      hours !== formData.school_grade_12_hours_served
    ) {
      setFormData((prev) => ({
        ...prev,
        school_grade_12_activities: activities,
        school_grade_12_contact_info: contacts,
        school_grade_12_hours_served: hours,
      }));
    }
  }, [
    grade12Rows,
    formData.school_grade_12_activities,
    formData.school_grade_12_contact_info,
    formData.school_grade_12_hours_served,
    setFormData,
  ]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
      className="academic-information-form"
    >
      <form className="form-group">
        {/* Title */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={800} textAlign="center">
            School Activities Documentation:
          </Typography>
        </Stack>

        {/* Original paragraph text (unchanged), just presented in Alert for consistency */}
        <Alert
          severity={hasAnyWarnings ? "warning" : "info"}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">
            Recipients of awards are often selected based on their level of
            involvement in activities. Sharing the details and extent of your
            involvement in school activities can strengthen your chances during
            the selection process.
          </Typography>
        </Alert>

        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            School Activities Documentation - Include your activities over the
            last three years – Grades 10, 11 and 12.
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Please note: Within the “hours served” box please note which of
            these categories applies to this activity: 10 – 50 hours, 50 – 100
            hours, 100 – 200 hours, or over 200 hours.
          </Typography>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Indicate all that apply */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>
            Indicate all that apply:
          </Typography>
        </Stack>

        <FormRow spacing={2}>
          <Grid item xs={12} md={6}>
            <Stack spacing={0.5}>
              <FormCheckbox
                label="Sports"
                checked={formData.school_activity_sports}
                onChange={handleChange("school_activity_sports")}
                name="school_activity_sports"
                isReadOnly={isReadOnly}
              />
              <FormCheckbox
                label="School Volunteer"
                checked={formData.school_activity_school_volunteer}
                onChange={handleChange("school_activity_school_volunteer")}
                name="school_activity_school_volunteer"
                isReadOnly={isReadOnly}
              />
              <FormCheckbox
                label="School Clubs"
                checked={formData.school_activity_school_clubs}
                onChange={handleChange("school_activity_school_clubs")}
                name="school_activity_school_clubs"
                isReadOnly={isReadOnly}
              />
              <FormCheckbox
                label="Students Council"
                checked={formData.school_activity_students_council}
                onChange={handleChange("school_activity_students_council")}
                name="school_activity_students_council"
                isReadOnly={isReadOnly}
              />
            </Stack>
          </Grid>
        </FormRow>

        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Enter below all activities in which you participated during each
            grade, including the capacity in which you were involved and time
            commitment. Include any leadership positions you held and
            achievements you have made in areas such as citizenship, sports, the
            arts, music groups, clubs, teams, etc. You may be required to verify
            your participation in the activities you have listed.
          </Typography>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Grade 10 */}
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>
            Grade 10
          </Typography>
        </Stack>

        <FormRow spacing={2}>
          <Grid item xs={12} md={12}>
            {grade10Rows.map((row, idx) => (
              <FormRow spacing={1} key={`sg10-${idx}`}>
                <Grid item xs={12} md={6}>
                  <FormInput
                    label={`Activity ${idx + 1}`}
                    value={row.activity}
                    onChange={(e) =>
                      setGrade10Rows((prev) => {
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], activity: e.target.value };
                        return copy;
                      })
                    }
                    multiline
                    rows={2}
                    disabled={isReadOnly}
                    status={getStatus("school_grade_10_activities", false)}
                    helperText={
                      idx === 0
                        ? stepErrors.school_grade_10_activities || ""
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={10} md={4}>
                  <FormInput
                    label="Contact name/phone number"
                    value={row.contact}
                    onChange={(e) =>
                      setGrade10Rows((prev) => {
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], contact: e.target.value };
                        return copy;
                      })
                    }
                    multiline
                    rows={2}
                    disabled={isReadOnly}
                    status={getStatus("school_grade_10_contact_info", false)}
                    helperText={
                      idx === 0
                        ? stepErrors.school_grade_10_contact_info || ""
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={1} md={1}>
                  <FormInput
                    label="Hours"
                    value={row.hours}
                    onChange={(e) =>
                      setGrade10Rows((prev) => {
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], hours: e.target.value };
                        return copy;
                      })
                    }
                    multiline
                    rows={2}
                    disabled={isReadOnly}
                    status={getStatus("school_grade_10_hours_served", false)}
                    helperText={
                      idx === 0
                        ? stepErrors.school_grade_10_hours_served || ""
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={1} md={1}>
                  <IconButton
                    onClick={() =>
                      setGrade10Rows((prev) => prev.filter((_, i) => i !== idx))
                    }
                    disabled={isReadOnly || grade10Rows.length === 1}
                    aria-label="remove"
                  >
                    <RemoveCircleOutlineIcon
                      color={grade10Rows.length === 1 ? "disabled" : "error"}
                    />
                  </IconButton>
                </Grid>
              </FormRow>
            ))}

            <Box sx={{ mt: 1 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() =>
                  setGrade10Rows((prev) => [
                    ...prev,
                    { activity: "", contact: "", hours: "" },
                  ])
                }
                disabled={isReadOnly}
                size="small"
              >
                Add Activity
              </Button>
            </Box>
          </Grid>
        </FormRow>

        <Divider sx={{ my: 3 }} />

        {/* Grade 11 */}
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>
            Grade 11
          </Typography>
        </Stack>

        <FormRow spacing={2}>
          <Grid item xs={12} md={12}>
            {grade11Rows.map((row, idx) => (
              <FormRow spacing={1} key={`sg11-${idx}`}>
                <Grid item xs={12} md={6}>
                  <FormInput
                    label={`Activity ${idx + 1}`}
                    value={row.activity}
                    onChange={(e) =>
                      setGrade11Rows((prev) => {
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], activity: e.target.value };
                        return copy;
                      })
                    }
                    multiline
                    rows={2}
                    disabled={isReadOnly}
                    status={getStatus("school_grade_11_activities", false)}
                    helperText={
                      idx === 0
                        ? stepErrors.school_grade_11_activities || ""
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={10} md={4}>
                  <FormInput
                    label="Contact name/phone number"
                    value={row.contact}
                    onChange={(e) =>
                      setGrade11Rows((prev) => {
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], contact: e.target.value };
                        return copy;
                      })
                    }
                    multiline
                    rows={2}
                    disabled={isReadOnly}
                    status={getStatus("school_grade_11_contact_info", false)}
                    helperText={
                      idx === 0
                        ? stepErrors.school_grade_11_contact_info || ""
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={1} md={1}>
                  <FormInput
                    label="Hours"
                    value={row.hours}
                    onChange={(e) =>
                      setGrade11Rows((prev) => {
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], hours: e.target.value };
                        return copy;
                      })
                    }
                    multiline
                    rows={2}
                    disabled={isReadOnly}
                    status={getStatus("school_grade_11_hours_served", false)}
                    helperText={
                      idx === 0
                        ? stepErrors.school_grade_11_hours_served || ""
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={1} md={1}>
                  <IconButton
                    onClick={() =>
                      setGrade11Rows((prev) => prev.filter((_, i) => i !== idx))
                    }
                    disabled={isReadOnly || grade11Rows.length === 1}
                    aria-label="remove"
                  >
                    <RemoveCircleOutlineIcon
                      color={grade11Rows.length === 1 ? "disabled" : "error"}
                    />
                  </IconButton>
                </Grid>
              </FormRow>
            ))}

            <Box sx={{ mt: 1 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() =>
                  setGrade11Rows((prev) => [
                    ...prev,
                    { activity: "", contact: "", hours: "" },
                  ])
                }
                disabled={isReadOnly}
                size="small"
              >
                Add Activity
              </Button>
            </Box>
          </Grid>
        </FormRow>

        <Divider sx={{ my: 3 }} />

        {/* Grade 12 */}
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={800}>
            Grade 12
          </Typography>
        </Stack>

        <FormRow spacing={2}>
          <Grid item xs={12} md={12}>
            {grade12Rows.map((row, idx) => (
              <FormRow spacing={1} key={`sg12-${idx}`}>
                <Grid item xs={12} md={6}>
                  <FormInput
                    label={`Activity ${idx + 1}`}
                    value={row.activity}
                    onChange={(e) =>
                      setGrade12Rows((prev) => {
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], activity: e.target.value };
                        return copy;
                      })
                    }
                    multiline
                    rows={2}
                    disabled={isReadOnly}
                    status={getStatus("school_grade_12_activities", false)}
                    helperText={
                      idx === 0
                        ? stepErrors.school_grade_12_activities || ""
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={10} md={4}>
                  <FormInput
                    label="Contact name/phone number"
                    value={row.contact}
                    onChange={(e) =>
                      setGrade12Rows((prev) => {
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], contact: e.target.value };
                        return copy;
                      })
                    }
                    multiline
                    rows={2}
                    disabled={isReadOnly}
                    status={getStatus("school_grade_12_contact_info", false)}
                    helperText={
                      idx === 0
                        ? stepErrors.school_grade_12_contact_info || ""
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={1} md={1}>
                  <FormInput
                    label="Hours"
                    value={row.hours}
                    onChange={(e) =>
                      setGrade12Rows((prev) => {
                        const copy = [...prev];
                        copy[idx] = { ...copy[idx], hours: e.target.value };
                        return copy;
                      })
                    }
                    multiline
                    rows={2}
                    disabled={isReadOnly}
                    status={getStatus("school_grade_12_hours_served", false)}
                    helperText={
                      idx === 0
                        ? stepErrors.school_grade_12_hours_served || ""
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={1} md={1}>
                  <IconButton
                    onClick={() =>
                      setGrade12Rows((prev) => prev.filter((_, i) => i !== idx))
                    }
                    disabled={isReadOnly || grade12Rows.length === 1}
                    aria-label="remove"
                  >
                    <RemoveCircleOutlineIcon
                      color={grade12Rows.length === 1 ? "disabled" : "error"}
                    />
                  </IconButton>
                </Grid>
              </FormRow>
            ))}

            <Box sx={{ mt: 1 }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() =>
                  setGrade12Rows((prev) => [
                    ...prev,
                    { activity: "", contact: "", hours: "" },
                  ])
                }
                disabled={isReadOnly}
                size="small"
              >
                Add Activity
              </Button>
            </Box>
          </Grid>
        </FormRow>
      </form>
    </Paper>
  );
}

export default memo(StepContent4);
